import React, { useEffect, useState, useCallback, useRef } from "react";
import API from "../../../services/api";

const VoterDirectory = () => {
  const [voters, setVoters] = useState([]);
  const [villList, setVillList] = useState([]);
  const [boothList, setBoothList] = useState([]);
  const [contactMap, setContactMap] = useState({});
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [villageId, setVillageId] = useState("");
  const [boothNo, setBoothNo] = useState("");
  const [gender, setGender] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRec, setTotalRec] = useState(0);
  const [showDetail, setShowDetail] = useState(false);
  const [detailVoter, setDetailVoter] = useState(null);
  const [exporting, setExporting] = useState(false);

  // Village autocomplete
  const [villSearch, setVillSearch] = useState("");
  const [villDrop, setVillDrop] = useState([]);
  const [showVillDrop, setShowVillDrop] = useState(false);
  const [selectedVill, setSelectedVill] = useState(null);
  const villRef = useRef(null);

  // ── Fetch Masters ─────────────────────────────────────────
  const fetchMasters = useCallback(async () => {
    try {
      const [vill, booths, contacts] = await Promise.all([
        API.get("/masters/villages"),
        API.get("/masters/booths"),
        API.get("/contacts/workers", { params: { page: 1, per_page: 500 } }),
      ]);
      setVillList(vill.data.data);
      setBoothList(booths.data.data);
      // Build epic_no → contact_type map
      const cmap = {};
      (contacts.data.data.contacts || []).forEach((c) => {
        if (c.epic_no) cmap[c.epic_no] = c.contact_type;
      });
      setContactMap(cmap);
    } catch (err) {
      console.error(err);
    }
  }, []);

  // ── Fetch Voters ──────────────────────────────────────────
  const fetchVoters = useCallback(
    async (page = 1) => {
      setLoading(true);
      try {
        const params = {
          page,
          per_page: 20,
          search: search || undefined,
          villageid: villageId || undefined,
          booth_no: boothNo || undefined,
          gender: gender || undefined,
        };
        const res = await API.get("/voters/", { params });
        setVoters(res.data.data.voters || []);
        setTotalPages(res.data.data.pagination.total_pages);
        setTotalRec(res.data.data.pagination.total);
        setCurrentPage(page);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    },
    [search, villageId, boothNo, gender],
  );

  useEffect(() => {
    fetchMasters();
  }, []);

  // ── Close dropdown outside click ──────────────────────────
  useEffect(() => {
    const h = (e) => {
      if (villRef.current && !villRef.current.contains(e.target))
        setShowVillDrop(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  // ── Village Autocomplete ──────────────────────────────────
  const handleVillSearch = (val) => {
    setVillSearch(val);
    setSelectedVill(null);
    setVillageId("");
    setBoothNo("");
    if (val.length >= 1) {
      setVillDrop(
        villList
          .filter((v) => v.name.toLowerCase().includes(val.toLowerCase()))
          .slice(0, 8),
      );
      setShowVillDrop(true);
    } else {
      setVillDrop([]);
      setShowVillDrop(false);
    }
  };

  const selectVillage = (v) => {
    setVillSearch(v.name);
    setSelectedVill(v);
    setVillageId(String(v.villageid));
    setBoothNo("");
    setShowVillDrop(false);
    setVillDrop([]);
  };

  const clearVillage = () => {
    setVillSearch("");
    setSelectedVill(null);
    setVillageId("");
    setBoothNo("");
    setVoters([]);
    setTotalRec(0);
  };

  // ── Filtered Booths for selected village ─────────────────
  const filteredBooths = selectedVill
    ? boothList.filter((b) => b.villageid === selectedVill.villageid)
    : [];

  // ── Handle Search ─────────────────────────────────────────
  const handleSearch = (e) => {
    e.preventDefault();
    fetchVoters(1);
  };

  const handleReset = () => {
    setSearch("");
    setGender("");
    setBoothNo("");
    clearVillage();
    setVoters([]);
    setTotalRec(0);
  };

  // ── Export to CSV ─────────────────────────────────────────
  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await API.get("/voters/", {
        params: {
          page: 1,
          per_page: 1000,
          search: search || undefined,
          villageid: villageId || undefined,
          booth_no: boothNo || undefined,
          gender: gender || undefined,
        },
      });
      const list = res.data.data.voters || [];

      // Build CSV
      const headers = [
        "Serial No",
        "EPIC No",
        "First Name (EN)",
        "Last Name (EN)",
        "First Name (KN)",
        "Last Name (KN)",
        "Gender",
        "Age",
        "Mobile",
        "Booth No",
        "House No",
        "Address",
        "Contact Type",
      ];
      const rows = list.map((v, i) => [
        i + 1,
        v.epic_no || "",
        v.firstname_en || "",
        v.lastname_en || "",
        v.firstname_v1 || "",
        v.lastname_v1 || "",
        v.gender || "",
        v.age || "",
        v.mobile || "",
        v.booth_no || "",
        v.house_no || "",
        `"${(v.address_en || "").replace(/"/g, '""')}"`,
        contactMap[v.epic_no] || "",
      ]);

      const csv = [headers, ...rows].map((r) => r.join(",")).join("\n");
      const blob = new Blob(["\uFEFF" + csv], {
        type: "text/csv;charset=utf-8;",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `voters_${selectedVill?.name || "all"}_${new Date().toISOString().split("T")[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert("Export failed");
    } finally {
      setExporting(false);
    }
  };

  const genderBadge = (g) => {
    if (g === "M") return "bg-primary-subtle text-primary";
    if (g === "F") return "bg-pink-subtle text-danger";
    return "bg-secondary-subtle text-secondary";
  };

  const CONTACT_COLORS = {
    Worker: "bg-primary-subtle text-primary",
    Leader: "bg-warning-subtle text-warning",
    "Booth Member": "bg-info-subtle text-info",
    "Booth President": "bg-success-subtle text-success",
    Volunteer: "bg-secondary-subtle text-secondary",
    Member: "bg-info-subtle text-info",
    "Vice President": "bg-danger-subtle text-danger",
    Secretary: "bg-warning-subtle text-warning",
    Treasurer: "bg-dark-subtle text-dark",
    President: "bg-success-subtle text-success",
  };

  return (
    <div>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
        <div>
          <h4 className="fw-bold mb-1">Voter Directory</h4>
          <p className="text-muted small mb-0">
            Search voters by village, booth, gender with contact type
          </p>
        </div>
        <button
          className="btn btn-success px-4"
          onClick={handleExport}
          disabled={exporting || voters.length === 0}
        >
          {exporting ? (
            <>
              <span className="spinner-border spinner-border-sm me-2"></span>
              Exporting...
            </>
          ) : (
            <>
              <i className="bi bi-file-earmark-excel me-2"></i>Export CSV
            </>
          )}
        </button>
      </div>

      {/* Filters */}
      <div className="card border-0 shadow-sm rounded-4 mb-3">
        <div className="card-body p-3">
          <form onSubmit={handleSearch}>
            <div className="row g-2 align-items-end">
              {/* Village Autocomplete */}
              <div className="col-12 col-md-3">
                <label className="form-label small text-muted mb-1">
                  Village
                </label>
                <div className="position-relative" ref={villRef}>
                  <div className="input-group">
                    <span className="input-group-text bg-white border-end-0">
                      <i className="bi bi-search text-muted small"></i>
                    </span>
                    <input
                      type="text"
                      className="form-control border-start-0"
                      placeholder="Type village..."
                      value={villSearch}
                      onChange={(e) => handleVillSearch(e.target.value)}
                      autoComplete="off"
                    />
                    {selectedVill && (
                      <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm"
                        onClick={clearVillage}
                      >
                        <i className="bi bi-x"></i>
                      </button>
                    )}
                  </div>
                  {showVillDrop && villDrop.length > 0 && (
                    <div
                      className="border rounded-3 shadow bg-white position-absolute w-100 mt-1"
                      style={{
                        zIndex: 9999,
                        maxHeight: "160px",
                        overflowY: "auto",
                      }}
                    >
                      {villDrop.map((v) => (
                        <div
                          key={v.villageid}
                          className="px-3 py-2 border-bottom d-flex align-items-center gap-2"
                          style={{ cursor: "pointer" }}
                          onMouseDown={() => selectVillage(v)}
                          onMouseEnter={(e) =>
                            (e.currentTarget.style.background = "#f0f4ff")
                          }
                          onMouseLeave={(e) =>
                            (e.currentTarget.style.background = "white")
                          }
                        >
                          <i className="bi bi-geo-alt text-muted small"></i>
                          <span className="small fw-medium">{v.name}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Booth */}
              <div className="col-6 col-md-2">
                <label className="form-label small text-muted mb-1">
                  Booth
                </label>
                <select
                  className="form-select"
                  value={boothNo}
                  onChange={(e) => setBoothNo(e.target.value)}
                  disabled={!selectedVill}
                >
                  <option value="">All Booths</option>
                  {filteredBooths.map((b) => (
                    <option key={b.boothid} value={b.booth_no}>
                      Booth {b.booth_no}
                      {b.booth_name ? ` - ${b.booth_name}` : ""}
                    </option>
                  ))}
                </select>
              </div>

              {/* Gender */}
              <div className="col-6 col-md-2">
                <label className="form-label small text-muted mb-1">
                  Gender
                </label>
                <select
                  className="form-select"
                  value={gender}
                  onChange={(e) => setGender(e.target.value)}
                >
                  <option value="">All Gender</option>
                  <option value="M">Male</option>
                  <option value="F">Female</option>
                  <option value="O">Other</option>
                </select>
              </div>

              {/* Search */}
              <div className="col-12 col-md-3">
                <label className="form-label small text-muted mb-1">
                  Search
                </label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Name, EPIC or mobile..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>

              {/* Buttons */}
              <div className="col-6 col-md-1">
                <button type="submit" className="btn btn-primary w-100">
                  <i className="bi bi-search"></i>
                </button>
              </div>
              <div className="col-6 col-md-1">
                <button
                  type="button"
                  className="btn btn-outline-secondary w-100"
                  onClick={handleReset}
                >
                  Reset
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>

      {/* Stats bar */}
      {totalRec > 0 && (
        <div className="d-flex gap-3 mb-3 flex-wrap">
          <span className="badge bg-primary-subtle text-primary px-3 py-2 rounded-pill">
            <i className="bi bi-people me-1"></i>
            {totalRec.toLocaleString("en-IN")} voters found
          </span>
          {selectedVill && (
            <span className="badge bg-success-subtle text-success px-3 py-2 rounded-pill">
              <i className="bi bi-geo-alt me-1"></i>
              {selectedVill.name}
            </span>
          )}
          {boothNo && (
            <span className="badge bg-warning-subtle text-warning px-3 py-2 rounded-pill">
              <i className="bi bi-grid me-1"></i>Booth {boothNo}
            </span>
          )}
          {gender && (
            <span className="badge bg-info-subtle text-info px-3 py-2 rounded-pill">
              {gender === "M"
                ? "♂ Male"
                : gender === "F"
                  ? "♀ Female"
                  : "Other"}
            </span>
          )}
        </div>
      )}

      {/* Table */}
      <div className="card border-0 shadow-sm rounded-4">
        <div className="card-body p-0">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary"></div>
            </div>
          ) : voters.length === 0 ? (
            <div className="text-center py-5 text-muted">
              <i className="bi bi-people fs-1 d-block mb-2"></i>
              {selectedVill || search
                ? "No voters found for selected filters"
                : "Select a village or search to view voters"}
            </div>
          ) : (
            <div className="table-responsive">
              <table
                className="table table-hover align-middle mb-0"
                style={{ fontSize: "13px" }}
              >
                <thead className="bg-light">
                  <tr>
                    <th className="px-4 py-3 small text-muted fw-semibold">
                      #
                    </th>
                    <th className="py-3 small text-muted fw-semibold">
                      EPIC No
                    </th>
                    <th className="py-3 small text-muted fw-semibold">Name</th>
                    <th className="py-3 small text-muted fw-semibold">
                      Gender
                    </th>
                    <th className="py-3 small text-muted fw-semibold">Age</th>
                    <th className="py-3 small text-muted fw-semibold">
                      Mobile
                    </th>
                    <th className="py-3 small text-muted fw-semibold">Booth</th>
                    <th className="py-3 small text-muted fw-semibold">
                      Contact Type
                    </th>
                    <th className="py-3 small text-muted fw-semibold text-end pe-4">
                      Detail
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {voters.map((v, idx) => {
                    const contactType = contactMap[v.epic_no];
                    return (
                      <tr key={v.voter_id}>
                        <td className="px-4 text-muted small">
                          {(currentPage - 1) * 20 + idx + 1}
                        </td>
                        <td>
                          <span className="badge bg-light text-dark border small">
                            {v.epic_no}
                          </span>
                        </td>
                        <td>
                          <div className="fw-semibold small">
                            {v.firstname_en} {v.lastname_en}
                          </div>
                          {(v.firstname_v1 || v.lastname_v1) && (
                            <div
                              className="text-muted"
                              style={{ fontSize: "11px" }}
                            >
                              {v.firstname_v1} {v.lastname_v1}
                            </div>
                          )}
                        </td>
                        <td>
                          <span
                            className={`badge small ${genderBadge(v.gender)}`}
                          >
                            {v.gender === "M"
                              ? "Male"
                              : v.gender === "F"
                                ? "Female"
                                : v.gender || "—"}
                          </span>
                        </td>
                        <td className="small text-muted">{v.age || "—"}</td>
                        <td>
                          {v.mobile ? (
                            <a
                              href={`tel:${v.mobile}`}
                              className="small text-decoration-none"
                            >
                              <i className="bi bi-telephone me-1 text-success"></i>
                              {v.mobile}
                            </a>
                          ) : (
                            <span className="text-muted small">—</span>
                          )}
                        </td>
                        <td>
                          <span className="badge bg-secondary-subtle text-secondary small">
                            Booth {v.booth_no}
                          </span>
                        </td>
                        <td>
                          {contactType ? (
                            <span
                              className={`badge small ${CONTACT_COLORS[contactType] || "bg-secondary-subtle text-secondary"}`}
                            >
                              {contactType}
                            </span>
                          ) : (
                            <span className="text-muted small">—</span>
                          )}
                        </td>
                        <td className="text-end pe-4">
                          <button
                            className="btn btn-sm btn-outline-info"
                            onClick={() => {
                              setDetailVoter(v);
                              setShowDetail(true);
                            }}
                          >
                            <i className="bi bi-eye"></i>
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="card-footer bg-white border-0 d-flex justify-content-between align-items-center py-3 px-4">
            <span className="text-muted small">
              Page {currentPage} of {totalPages} ·{" "}
              {totalRec.toLocaleString("en-IN")} total
            </span>
            <nav>
              <ul className="pagination pagination-sm mb-0">
                <li
                  className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                >
                  <button
                    className="page-link"
                    onClick={() => fetchVoters(currentPage - 1)}
                  >
                    <i className="bi bi-chevron-left"></i>
                  </button>
                </li>
                {[...Array(Math.min(totalPages, 5))].map((_, i) => {
                  const page = currentPage <= 3 ? i + 1 : currentPage - 2 + i;
                  if (page > totalPages) return null;
                  return (
                    <li
                      key={page}
                      className={`page-item ${currentPage === page ? "active" : ""}`}
                    >
                      <button
                        className="page-link"
                        onClick={() => fetchVoters(page)}
                      >
                        {page}
                      </button>
                    </li>
                  );
                })}
                <li
                  className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}
                >
                  <button
                    className="page-link"
                    onClick={() => fetchVoters(currentPage + 1)}
                  >
                    <i className="bi bi-chevron-right"></i>
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        )}
      </div>

      {/* Voter Detail Modal */}
      {showDetail && detailVoter && (
        <div
          className="modal show d-block"
          style={{ background: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered modal-md">
            <div className="modal-content border-0 rounded-4 shadow-lg">
              <div
                className="modal-header border-0 px-4 pt-4 pb-0"
                style={{
                  background: "linear-gradient(135deg,#1a3c5e,#2d6a4f)",
                  borderRadius: "1rem 1rem 0 0",
                }}
              >
                <div>
                  <h5 className="text-white fw-bold mb-1">
                    {detailVoter.firstname_en} {detailVoter.lastname_en}
                  </h5>
                  {(detailVoter.firstname_v1 || detailVoter.lastname_v1) && (
                    <div className="text-white text-opacity-75 small">
                      {detailVoter.firstname_v1} {detailVoter.lastname_v1}
                    </div>
                  )}
                </div>
                <button
                  className="btn-close btn-close-white"
                  onClick={() => setShowDetail(false)}
                ></button>
              </div>
              <div className="modal-body px-4 py-3">
                <div className="row g-3">
                  {[
                    { label: "EPIC No", value: detailVoter.epic_no },
                    { label: "Mobile", value: detailVoter.mobile },
                    {
                      label: "Gender",
                      value:
                        detailVoter.gender === "M"
                          ? "Male"
                          : detailVoter.gender === "F"
                            ? "Female"
                            : detailVoter.gender,
                    },
                    { label: "Age", value: detailVoter.age },
                    { label: "DOB", value: detailVoter.dob },
                    { label: "Booth No", value: detailVoter.booth_no },
                    { label: "House No", value: detailVoter.house_no },
                    { label: "Section No", value: detailVoter.section_no },
                    { label: "Serial No", value: detailVoter.serial_no },
                  ].map((item) => (
                    <div key={item.label} className="col-6">
                      <div className="text-muted small">{item.label}</div>
                      <div className="fw-medium small">{item.value || "—"}</div>
                    </div>
                  ))}
                  {detailVoter.address_en && (
                    <div className="col-12">
                      <div className="text-muted small">Address</div>
                      <div className="small">{detailVoter.address_en}</div>
                    </div>
                  )}
                  {detailVoter.ps_name_en && (
                    <div className="col-12">
                      <div className="text-muted small">Polling Station</div>
                      <div className="small">{detailVoter.ps_name_en}</div>
                    </div>
                  )}
                  {/* Contact Type */}
                  <div className="col-12">
                    <div className="text-muted small mb-1">Contact Type</div>
                    {contactMap[detailVoter.epic_no] ? (
                      <span
                        className={`badge ${CONTACT_COLORS[contactMap[detailVoter.epic_no]] || ""}`}
                      >
                        {contactMap[detailVoter.epic_no]}
                      </span>
                    ) : (
                      <span className="text-muted small">
                        Not a contact yet
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="modal-footer border-0 px-4 pb-4">
                {detailVoter.mobile && (
                  <a
                    href={`tel:${detailVoter.mobile}`}
                    className="btn btn-success px-4"
                  >
                    <i className="bi bi-telephone me-2"></i>Call
                  </a>
                )}
                <button
                  className="btn btn-light px-4"
                  onClick={() => setShowDetail(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VoterDirectory;
